import { createClient } from '@sanity/client';

// Sanity client configuration
export const client = createClient({
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID || 'w1g1c7ep', // Original Pancake House Sanity project ID
  dataset: import.meta.env.VITE_SANITY_DATASET || 'production',
  useCdn: import.meta.env.VITE_SANITY_USE_CDN !== 'false', // Use CDN in production
  apiVersion: '2023-05-03', // Use the latest API version
  token: import.meta.env.VITE_SANITY_TOKEN, // Use token from environment variables
  ignoreBrowserTokenWarning: true // Ignore warnings about tokens in browser
});


// Test function to verify Sanity connection
export async function testSanityConnection() {
  try {
    // Simple query to test connection
    const result = await client.fetch(`*[_type == "menuCategory"][0...1]`);
    return { success: true, data: result };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Fetch menu categories from Sanity
 * @returns {Promise<Array>} Array of menu categories with title, description, and image
 */
export async function getMenuCategories() {
  try {
    // Updated query to explicitly exclude drafts
    const categories = await client.fetch(`
      *[_type == "menuCategory" && !(_id match "drafts.*") && (!defined(enabled) || enabled == true)] | order(order asc) {
        _id,
        name,
        description,
        "slug": slug.current,
        "imageUrl": image.asset->url
      }
    `);
    
    // Debug: Log categories to check for duplicates
    console.log('All categories from Sanity:', categories.map(c => ({ id: c._id, name: c.name, slug: c.slug })));
    
    // Remove duplicates by slug (in case both draft and published exist)
    const uniqueCategories = categories.reduce((acc, category) => {
      const existingIndex = acc.findIndex(c => c.slug === category.slug);
      if (existingIndex === -1) {
        acc.push(category);
      } else {
        // If duplicate found, prefer the one without 'drafts.' prefix
        if (!category._id.startsWith('drafts.')) {
          acc[existingIndex] = category;
        }
        console.warn(`Duplicate category found: ${category.name} (${category._id})`);
      }
      return acc;
    }, []);
    
    console.log('Unique categories after deduplication:', uniqueCategories.map(c => ({ id: c._id, name: c.name, slug: c.slug })));
    
    return uniqueCategories;
  } catch (error) {
    console.error('Error fetching menu categories:', error);
    return [];
  }
}

/**
 * Fetch a specific menu category by ID
 * @param {string} id - The ID of the menu category
 * @returns {Promise<Object>} Menu category with title, description, and image
 */
export async function getMenuCategoryById(id) {
  try {
    return await client.fetch(`
      *[_type == "menuCategory" && !(_id in path('drafts.**')) && _id == $id][0] {
        _id,
        name,
        description,
        "slug": slug.current,
        "imageUrl": image.asset->url
      }
    `, { id });
  } catch (error) {
    return null;
  }
}

/**
 * Fetch menu items for a specific category
 * @param {string} categoryId - The ID of the category
 * @returns {Promise<Array>} Array of menu items in the category
 */
export async function getMenuItemsByCategory(categoryId) {
  try {
    return await client.fetch(`
      *[_type == "menuItem" && !(_id in path('drafts.**')) && references($categoryId) && (!defined(enabled) || enabled == true)] {
        _id,
        name,
        description,
        calories,
        "slug": slug.current,
        "imageUrl": image.asset->url,
        "plainDescription": pt::text(description),
        "category": *[_type == "menuCategory" && !(_id in path('drafts.**')) && _id == $categoryId][0] {
          _id,
          name,
          "slug": slug.current
        }
      }
    `, { categoryId });
  } catch (error) {
    return [];
  }
}

/**
 * Fetch all locations with coordinates
 * @returns {Promise<Array>} Array of locations with coordinates
 */
export async function getLocations() {
  try {
    const locations = await client.fetch(`
      *[_type == "location" && !(_id in path('drafts.**'))] {
        _id,
        name,
        region,
        "slug": slug.current,
        address {
          street,
          city,
          state,
          zipCode
        },
        phoneNumber,
        coordinates {
          lat,
          lng
        },
        "imageUrl": featuredImage.asset->url,
        onlineOrderingUrl,
        onlineReservationsUrl
      }
    `);
    return locations;
  } catch (error) {
    return [];
  }
}

/**
 * Fetch locations grouped by region
 * @returns {Promise<Object>} Object with regions as keys and arrays of locations as values
 */
export async function getLocationsByRegion() {
  try {
    const locations = await getLocations();
    
    // Group locations by region
    const locationsByRegion = locations.reduce((acc, location) => {
      const region = location.region || 'other';
      if (!acc[region]) {
        acc[region] = [];
      }
      acc[region].push(location);
      return acc;
    }, {});
    
    return locationsByRegion;
  } catch (error) {
    return {};
  }
}

/**
 * Fetch a single location by slug
 * @param {string} slug - The slug of the location
 * @returns {Promise<Object>} Location data
 */
export async function getLocationBySlug(slug) {
  try {
    const location = await client.fetch(`
      *[_type == "location" && !(_id in path('drafts.**')) && slug.current == $slug][0] {
        _id,
        name,
        region,
        "slug": slug.current,
        address {
          street,
          city,
          state,
          zipCode
        },
        phoneNumber,
        additionalInformation {
          patioSeating,
          privateRooms
        },
        description,
        hoursOfOperation[] {
          day,
          openTime,
          closeTime,
          isClosed
        },
        coordinates {
          lat,
          lng
        },
        "featuredImage": featuredImage.asset->url,
        onlineOrderingUrl,
        onlineReservationsUrl
      }
    `, { slug });
    return location;
  } catch (error) {
    return null;
  }
}

/**
 * Fetch a menu item by slug
 * @param {string} slug - The slug of the menu item
 * @returns {Promise<Object>} Menu item data
 */
export async function getMenuItemBySlug(slug) {
  try {
    const menuItem = await client.fetch(`
      *[_type == "menuItem" && !(_id in path('drafts.**')) && slug.current == $slug && (!defined(enabled) || enabled == true) && (!defined(category->enabled) || category->enabled == true)][0] {
        _id,
        name,
        description,
        calories,
        featured,
        order,
        "slug": slug.current,
        "imageUrl": image.asset->url,
        "plainDescription": pt::text(description),
        dietary,
        regions,
        "category": category-> {
          _id,
          name,
          "slug": slug.current
        },
        "locations": *[_type == "location"] {
          _id,
          name,
          "slug": slug.current
        }
      }
    `, { slug });
    return menuItem;
  } catch (error) {
    return null;
  }
}

/**
 * Fetch all menu items with their categories
 * @returns {Promise<Array>} Array of menu items with category information
 */
export async function getAllMenuItems() {
  try {
    const menuItems = await client.fetch(`
      *[_type == "menuItem" && !(_id in path('drafts.**')) && (!defined(enabled) || enabled == true) && (!defined(category->enabled) || category->enabled == true)] {
        _id,
        name,
        description,
        calories,
        featured,
        order,
        "slug": slug.current,
        "imageUrl": image.asset->url,
        "plainDescription": pt::text(description),
        "categoryId": category._ref,
        "category": category-> {
          _id,
          name,
          "slug": slug.current
        }
      }
    `);
    return menuItems;
  } catch (error) {
    return [];
  }
}

/**
 * Fetch menu items by category slug
 * @param {string} categorySlug - The slug of the category
 * @returns {Promise<Object>} Object containing category details and menu items
 */
export async function getMenuItemsByCategorySlug(categorySlug) {
  try {
    const result = await client.fetch(`
      {
        "category": *[_type == "menuCategory" && !(_id in path('drafts.**')) && slug.current == $categorySlug && (!defined(enabled) || enabled == true)][0] {
          _id,
          name,
          description,
          "slug": slug.current,
          "imageUrl": image.asset->url
        },
        "menuItems": *[_type == "menuItem" && !(_id in path('drafts.**')) && (!defined(enabled) || enabled == true) && references(*[_type == "menuCategory" && !(_id in path('drafts.**')) && slug.current == $categorySlug && (!defined(enabled) || enabled == true)]._id)] {
          _id,
          name,
          description,
          calories,
          featured,
          order,
          "slug": slug.current,
          "imageUrl": image.asset->url,
          "plainDescription": pt::text(description)
        }
      }
    `, { categorySlug });
    
    return result;
  } catch (error) {
    return { category: null, menuItems: [] };
  }
}

/**
 * Fetch related menu items from the same category
 * @param {string} itemId - The ID of the current menu item
 * @param {string} categoryId - The ID of the category
 * @param {number} limit - Maximum number of related items to return
 * @returns {Promise<Array>} Array of related menu items
 */
export async function getRelatedMenuItems(itemId, categoryId, limit = 3) {
  try {
    const relatedItems = await client.fetch(`
      *[_type == "menuItem" && !(_id in path('drafts.**')) && references($categoryId) && _id != $itemId && (!defined(enabled) || enabled == true) && (!defined(category->enabled) || category->enabled == true)][0..${limit-1}] {
        _id,
        name,
        description,
        calories,
        "slug": slug.current,
        "imageUrl": image.asset->url,
        "plainDescription": pt::text(description)
      }
    `, { itemId, categoryId });
    
    return relatedItems;
  } catch (error) {
    return [];
  }
}

/**
 * Submit a contact form via Firebase Function (secure)
 * @param {Object} formData - The form data to submit
 * @returns {Promise<Object>} The response from the API
 */
export async function submitContactForm(formData) {
  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || '',
        location: formData.location || '',
        message: formData.message,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return { 
        success: false, 
        error: result.error || 'Failed to submit form' 
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error submitting contact form:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Submit a store request form via Firebase Function (secure)
 * @param {Object} formData - The form data to submit
 * @returns {Promise<Object>} The response from the API
 */
export async function submitStoreRequest(formData) {
  try {
    const response = await fetch('/api/store-request', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        store: formData.store,
        storeName: formData.storeName,
        email: formData.email,
        burritoStickers: formData.burritoStickers || 0,
        gfStickers: formData.gfStickers || 0,
        ojStickers: formData.ojStickers || 0,
        regularCoffeeStickers: formData.regularCoffeeStickers || 0,
        decafCoffeeStickers: formData.decafCoffeeStickers || 0,
        kidMenus: formData.kidMenus || 0,
        gmBusinessCards: formData.gmBusinessCards || 0,
        bohHats: formData.bohHats || 0,
        giftCards: formData.giftCards || 0,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return { 
        success: false, 
        error: result.error || 'Failed to submit store request' 
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error submitting store request:', error);
    return { success: false, error: error.message };
  }
}